"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings, Globe, Bell, Check, ExternalLink, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDemo } from "@/lib/demo-context";

export default function SettingsPage() {
  const { demoMode, setDemoMode } = useDemo();

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configure your workspace and integrations
          </p>
        </div>
      </div>

      <div className="flex-1 p-8 overflow-auto">
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Workspace</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Workspace Name</Label>
                    <Input defaultValue="Social Standards" className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Workspace URL</Label>
                    <Input defaultValue="social-standards" className="h-9" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Default Timezone</Label>
                  <Select defaultValue="est">
                    <SelectTrigger className="w-[300px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="est">Eastern Time (EST/EDT)</SelectItem>
                      <SelectItem value="cst">Central Time (CST/CDT)</SelectItem>
                      <SelectItem value="mst">Mountain Time (MST/MDT)</SelectItem>
                      <SelectItem value="pst">Pacific Time (PST/PDT)</SelectItem>
                      <SelectItem value="utc">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Separator />
                <div className="flex justify-end">
                  <Button size="sm">Save Changes</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Default Post Settings</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">Require approval before publishing</Label>
                  <Select defaultValue="yes">
                    <SelectTrigger className="w-[300px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes - all posts need approval</SelectItem>
                      <SelectItem value="members">Only for Members and Viewers</SelectItem>
                      <SelectItem value="no">No - anyone can publish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Default posting time</Label>
                  <Input type="time" defaultValue="09:00" className="w-[200px] h-9" />
                </div>
                <Separator />
                <div className="flex justify-end">
                  <Button size="sm">Save Changes</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Email Notifications</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {[
                  { label: "Post approved", desc: "When your post is approved by a team member", enabled: true },
                  { label: "Post rejected", desc: "When your post is rejected with feedback", enabled: true },
                  { label: "Post published", desc: "When a scheduled post is published successfully", enabled: false },
                  { label: "Post failed", desc: "When a post fails to publish", enabled: true },
                  { label: "New comment", desc: "When someone comments on your post", enabled: true },
                  { label: "Team invite accepted", desc: "When someone accepts your invite", enabled: false },
                ].map((notification) => (
                  <div
                    key={notification.label}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium">{notification.label}</p>
                      <p className="text-xs text-muted-foreground">{notification.desc}</p>
                    </div>
                    <button
                      className={cn(
                        "h-6 w-11 rounded-full transition-colors relative",
                        notification.enabled ? "bg-primary" : "bg-muted"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
                          notification.enabled ? "left-[22px]" : "left-0.5"
                        )}
                      />
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Current Plan</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-bold">Free Plan</p>
                      <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Current</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      5 social accounts &middot; 3 team members &middot; 30 scheduled posts/month
                    </p>
                  </div>
                  <Button>Upgrade to Pro</Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="p-4 rounded-lg border">
                    <p className="text-sm font-semibold">Pro</p>
                    <p className="text-2xl font-bold mt-1">$29<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                    <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Unlimited social accounts</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> 10 team members</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Unlimited scheduled posts</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Advanced analytics</li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-lg border">
                    <p className="text-sm font-semibold">Enterprise</p>
                    <p className="text-2xl font-bold mt-1">$99<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                    <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Everything in Pro</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Unlimited team members</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Custom approval workflows</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Priority support</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
